import { themeConfig } from "../configs/theme.config";
import type {
  LAYOUT_TYPE_BLANK,
  LAYOUT_TYPE_SIMPLE,
} from "../constants/theme.constant";
import {
  LAYOUT_TYPE_CLASSIC,
  LAYOUT_TYPE_DECKED,
  LAYOUT_TYPE_LOADING,
  LAYOUT_TYPE_MODERN,
  LAYOUT_TYPE_STACKED_SIDE,
  MODE_DARK,
  MODE_LIGHT,
  NAV_MODE_DARK,
  NAV_MODE_LIGHT,
  NAV_MODE_THEMED,
  NAV_MODE_TRANSPARENT,
} from "../constants/theme.constant";

export type ThemeDirection = "rtl" | "ltr";
export type ThemeMode = "dark" | "light";
export type ThemeLayoutType =
  | typeof LAYOUT_TYPE_CLASSIC
  | typeof LAYOUT_TYPE_MODERN
  | typeof LAYOUT_TYPE_STACKED_SIDE
  | typeof LAYOUT_TYPE_DECKED
  | typeof LAYOUT_TYPE_SIMPLE
  | typeof LAYOUT_TYPE_LOADING
  | typeof LAYOUT_TYPE_BLANK;
export type ThemeNavMode =
  | typeof NAV_MODE_LIGHT
  | typeof NAV_MODE_DARK
  | typeof NAV_MODE_THEMED
  | typeof NAV_MODE_TRANSPARENT;

export type ThemeLayout = {
  type: ThemeLayoutType;
  sideNavCollapse: boolean;
  previousType?: ThemeLayoutType;
};

export type ThemeState = {
  themeColor: string;
  direction: ThemeDirection;
  mode: ThemeMode;
  locale: string;
  primaryColorLevel: number;
  panelExpand: boolean;
  cardBordered: boolean;
  navMode: ThemeNavMode;
  layout: ThemeLayout;
};

const availableNavColorLayouts: ThemeLayoutType[] = [
  LAYOUT_TYPE_CLASSIC,
  LAYOUT_TYPE_STACKED_SIDE,
  LAYOUT_TYPE_DECKED,
];

export function getInitialNavMode(): ThemeNavMode {
  if (
    themeConfig.layout.type === LAYOUT_TYPE_MODERN &&
    themeConfig.navMode !== NAV_MODE_THEMED
  ) {
    return NAV_MODE_TRANSPARENT;
  }

  return themeConfig.navMode as ThemeNavMode;
}

export const initialThemeState: ThemeState = {
  themeColor: themeConfig.themeColor,
  direction: themeConfig.direction as ThemeDirection,
  mode: themeConfig.mode as ThemeMode,
  locale: themeConfig.locale,
  primaryColorLevel: themeConfig.primaryColorLevel,
  panelExpand: themeConfig.panelExpand,
  cardBordered: themeConfig.cardBordered,
  navMode: getInitialNavMode(),
  layout: {
    type: themeConfig.layout.type as ThemeLayoutType,
    sideNavCollapse: themeConfig.layout.sideNavCollapse,
    previousType: undefined,
  },
};

export type ThemeAction =
  | { type: "setDirection"; payload: ThemeDirection }
  | { type: "setMode"; payload: ThemeMode }
  | { type: "setLocale"; payload: string }
  | { type: "setLayout"; payload: ThemeLayoutType }
  | { type: "setPreviousLayout"; payload: ThemeLayoutType | undefined }
  | { type: "setLoadingLayout" }
  | { type: "setSideNavCollapse"; payload: boolean }
  | { type: "setNavMode"; payload: ThemeNavMode | "default" }
  | { type: "setPanelExpand"; payload: boolean }
  | { type: "setThemeColor"; payload: string }
  | { type: "setThemeColorLevel"; payload: number };

export function themeReducer(
  state: ThemeState,
  action: ThemeAction,
): ThemeState {
  switch (action.type) {
    case "setDirection":
      return { ...state, direction: action.payload };
    case "setMode": {
      const availableColorNav = availableNavColorLayouts.includes(
        state.layout.type,
      );
      let navMode = state.navMode;
      if (availableColorNav && state.navMode !== NAV_MODE_THEMED) {
        if (action.payload === MODE_DARK) {
          navMode = NAV_MODE_DARK;
        }
        if (action.payload === MODE_LIGHT) {
          navMode = NAV_MODE_LIGHT;
        }
      }

      return { ...state, mode: action.payload, navMode };
    }
    case "setLocale":
      return { ...state, locale: action.payload };
    case "setLayout": {
      let navMode = state.navMode;
      if (action.payload === LAYOUT_TYPE_MODERN) {
        navMode = NAV_MODE_TRANSPARENT;
      }

      const availableColorNav = availableNavColorLayouts.includes(
        action.payload,
      );
      if (availableColorNav && state.mode === MODE_LIGHT) {
        navMode = NAV_MODE_LIGHT;
      }
      if (availableColorNav && state.mode === MODE_DARK) {
        navMode = NAV_MODE_DARK;
      }

      return {
        ...state,
        cardBordered: action.payload === LAYOUT_TYPE_MODERN,
        navMode,
        layout: { ...state.layout, type: action.payload },
      };
    }
    case "setPreviousLayout":
      return {
        ...state,
        layout: { ...state.layout, previousType: action.payload },
      };
    case "setLoadingLayout":
      return {
        ...state,
        cardBordered: false,
        layout: {
          ...state.layout,
          type: LAYOUT_TYPE_LOADING,
          previousType: undefined,
        },
      };
    case "setSideNavCollapse":
      return {
        ...state,
        layout: { ...state.layout, sideNavCollapse: action.payload },
      };
    case "setNavMode": {
      if (action.payload !== "default") {
        return { ...state, navMode: action.payload };
      }

      let navMode = state.navMode;
      if (state.layout.type === LAYOUT_TYPE_MODERN) {
        navMode = NAV_MODE_TRANSPARENT;
      }

      const availableColorNav = availableNavColorLayouts.includes(
        state.layout.type,
      );
      if (availableColorNav && state.mode === MODE_LIGHT) {
        navMode = NAV_MODE_LIGHT;
      }
      if (availableColorNav && state.mode === MODE_DARK) {
        navMode = NAV_MODE_DARK;
      }

      return { ...state, navMode };
    }
    case "setPanelExpand":
      return { ...state, panelExpand: action.payload };
    case "setThemeColor":
      return { ...state, themeColor: action.payload };
    case "setThemeColorLevel":
      return { ...state, primaryColorLevel: action.payload };
    default:
      return state;
  }
}
